<template>
  <button
    class="ca-base-button"
    :class="[
      `ca-base-button--${type}-${variant}`,
      {
        'ca-base-button--icon-left': $slots.trailingIcon,
        'ca-base-button--icon-right': $slots.leadingIcon,
      },
    ]"
    :disabled="disabled"
  >
    <div v-if="$slots.trailingIcon" class="ca-base-button__trailing-icon">
      <slot name="trailingIcon" />
    </div>
    <span class="ca-base-button__label">{{ label }}</span>
    <div v-if="$slots.leadingIcon" class="ca-base-button__leading-icon">
      <slot name="leadingIcon" />
    </div>
  </button>
</template>

<script setup>
defineOptions({
  name: "CaBaseButton",
});

defineProps({
  /** Whether the button is disabled. */
  disabled: {
    type: Boolean,
    default: false,
  },
  /** The label text displayed on the button. */
  label: {
    type: String,
    default: "Label",
  },
  /** Type: 'theme', 'inverted', or 'danger'. */
  type: {
    type: String,
    default: "theme",
    validator: (v) => ["theme", "inverted", "danger"].includes(v),
  },
  /** Variant: 'primary', 'secondary', or 'tertiary'. */
  variant: {
    type: String,
    default: "primary",
    validator: (v) => ["primary", "secondary", "tertiary"].includes(v),
  },
});
</script>

<style scoped lang="scss">
// ---------------------------------------------------------------------------
// Base
// ---------------------------------------------------------------------------
.ca-base-button {
  display: flex;
  align-items: center;
  justify-content: center;
  height: var(--size-8);
  padding: 0 var(--size-5);
  border-radius: var(--size-3);
  border: 1px solid transparent;
  cursor: pointer;
  transition:
    background-color 0.2s ease,
    border-color 0.2s ease,
    color 0.2s ease;

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }

  &:focus-visible {
    outline: 1px solid var(--ca-color-feedback-info);
    outline-offset: 1px;
  }
}

// Icon padding adjustments
.ca-base-button--icon-left {
  padding-left: var(--size-4);
}

.ca-base-button--icon-right {
  padding-right: var(--size-4);
}

.ca-base-button__label {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: 600;
}

.ca-base-button__trailing-icon {
  display: flex;
  align-items: center;
}

.ca-base-button__leading-icon {
  display: flex;
  align-items: center;
  margin-left: var(--size-3);
}

// ---------------------------------------------------------------------------
// Theme — Primary
// ---------------------------------------------------------------------------
.ca-base-button--theme-primary {
  background-color: var(--ca-color-theme-primary);
  border-color: var(--ca-color-theme-primary);
  color: var(--ca-color-text-inverted);

  &:hover:not(:disabled) {
    background-color: var(--ca-color-theme-primary-60);
    border-color: var(--ca-color-theme-primary-60);
  }

  &:active:not(:disabled) {
    background-color: var(--ca-color-theme-primary-50);
    border-color: var(--ca-color-theme-primary-50);
  }
}

// ---------------------------------------------------------------------------
// Theme — Secondary
// ---------------------------------------------------------------------------
.ca-base-button--theme-secondary {
  background-color: transparent;
  border-color: var(--ca-color-theme-primary);
  color: var(--ca-color-text-body);

  &:hover:not(:disabled) {
    background-color: var(--ca-black-20);
    border-color: var(--ca-color-theme-primary-60);
  }

  &:active:not(:disabled) {
    background-color: var(--ca-black-30);
    border-color: var(--ca-color-theme-primary);
  }
}

// ---------------------------------------------------------------------------
// Theme — Tertiary
// ---------------------------------------------------------------------------
.ca-base-button--theme-tertiary {
  background-color: transparent;
  border-color: transparent;
  color: var(--ca-color-text-body);

  &:hover:not(:disabled) {
    background-color: var(--ca-color-neutrals-on-background-high-20);
  }

  &:active:not(:disabled) {
    background-color: var(--ca-color-neutrals-on-background-high-30);
  }
}

// ---------------------------------------------------------------------------
// Inverted — Primary
// ---------------------------------------------------------------------------
.ca-base-button--inverted-primary {
  background-color: var(--ca-color-text-inverted);
  border-color: transparent;
  color: var(--ca-color-text-body);

  &:hover:not(:disabled) {
    background-color: var(--ca-color-text-inverted-60);
  }

  &:active:not(:disabled) {
    background-color: var(--ca-color-text-inverted-30);
  }
}

// ---------------------------------------------------------------------------
// Inverted — Secondary
// ---------------------------------------------------------------------------
.ca-base-button--inverted-secondary {
  background-color: transparent;
  border-color: var(--ca-color-text-inverted);
  color: var(--ca-color-text-inverted);

  &:hover:not(:disabled) {
    background-color: var(--ca-color-text-inverted-20);
  }

  &:active:not(:disabled) {
    background-color: var(--ca-color-text-inverted-30);
  }
}

// ---------------------------------------------------------------------------
// Inverted — Tertiary
// ---------------------------------------------------------------------------
.ca-base-button--inverted-tertiary {
  background-color: transparent;
  border-color: transparent;
  color: var(--ca-color-text-inverted);

  &:hover:not(:disabled) {
    background-color: var(--ca-color-neutrals-inverted-20);
  }

  &:active:not(:disabled) {
    background-color: var(--ca-color-neutrals-inverted-30);
  }
}

// ---------------------------------------------------------------------------
// Danger — Primary
// ---------------------------------------------------------------------------
.ca-base-button--danger-primary {
  background-color: var(--ca-dark-error);
  border-color: transparent;
  color: var(--ca-color-text-inverted);

  &:hover:not(:disabled) {
    background-color: var(--ca-error);
  }

  &:active:not(:disabled) {
    background-color: var(--ca-error-20);
  }
}

// ---------------------------------------------------------------------------
// Danger — Secondary
// ---------------------------------------------------------------------------
.ca-base-button--danger-secondary {
  background-color: transparent;
  border-color: var(--ca-dark-error);
  color: var(--ca-dark-error);

  &:hover:not(:disabled) {
    border-color: var(--ca-error);
    color: var(--ca-error);
  }

  &:active:not(:disabled) {
    border-color: var(--ca-dark-error);
    color: var(--ca-dark-error);
  }
}

// ---------------------------------------------------------------------------
// Danger — Tertiary
// ---------------------------------------------------------------------------
.ca-base-button--danger-tertiary {
  background-color: transparent;
  border-color: transparent;
  color: var(--ca-dark-error);

  &:hover:not(:disabled) {
    background-color: var(--ca-black-20);
  }

  &:active:not(:disabled) {
    background-color: var(--ca-black-30);
  }
}
</style>
